import { Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CDLineItemValidation, MaterialTypes } from 'app/core/store/contract-details/contract-details.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { FlorderDetail, Material, Planning } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { MaterialFlowPanelStatus, PanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { Destroyable } from 'app/core/util/destroyable';
import { ScrollService } from 'app/core/services/scroll.service';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { sleep$$ } from '../../../core/util/util';

@Directive()
export abstract class FlorderEditDetailComponent<MATERIAL extends Material, FLORDERDETAIL extends FlorderDetail<MATERIAL, PLANNING>, PLANNING extends Planning> extends Destroyable implements OnInit, OnChanges {
  @Input() lineItemValidation?: CDLineItemValidation;
  @Input() materialTypes: MaterialTypes;
  @Input() editMaterial: MATERIAL;
  @Input() availablePickingDates: { full: string[]; nonFull: string[] };
  @Input() definitions: Definitions;
  @Input() panelStatus: PanelStatus;
  @Input() panelStatusMaterial: MaterialFlowPanelStatus;

  @Output() cancelClicked = new EventEmitter(); // Go back to orders-list
  @Output() saveMaterialClicked = new EventEmitter<MATERIAL>(); // Store material data in state
  @Output() startEditMaterialClicked = new EventEmitter<MATERIAL>(); // Start editing material
  @Output() removeMaterialClicked = new EventEmitter<MATERIAL>(); // Remove material from state
  @Output() nextClicked = new EventEmitter();
  @Output() backClicked = new EventEmitter();
  @Output() formValidChanged = new EventEmitter();
  @Output() showQuantityModal = new EventEmitter();

  abstract get florderDetail(): FLORDERDETAIL;
  private valid$ = new Subject<boolean>();
  private editStatus = new BehaviorSubject<boolean>(false);

  nextOnEnterPressed$ = new BehaviorSubject(true);

  constructor(protected scrollService: ScrollService) {
    super();
    this.editStatus
      .asObservable()
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        filter(isEdit => isEdit)
      )
      .subscribe(() => {
        this.scrollService.scrollToElement('step-details');
      });
  }

  ngOnInit() {
    this.valid$
      .asObservable()
      .pipe(distinctUntilChanged())
      .subscribe(valid => {
        this.formValidChanged.emit(valid);
      });
    this.isValid(); // Trigger valid$ to be updated
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.panelStatus) {
      if (this.panelStatus) {
        this.editStatus.next(this.panelStatus.isEdit);
      } else {
        this.editStatus.next(false);
      }
    }
  }

  isValid(): boolean {
    const valid = this.florderDetail.materials.length > 0;
    this.valid$.next(valid);
    return valid;
  }

  async nextClickedFn() {
    if (this.isValid()) {
      this.nextOnEnterPressed$.next(false);
      this.nextClicked.emit();
      await sleep$$(10); // make sure that this event is done before activating submit on the next button
      this.nextOnEnterPressed$.next(true);
    }
  }

  async saveMaterialClickedFn(material) {
    this.nextOnEnterPressed$.next(false);
    this.saveMaterialClicked.emit(material);
    await sleep$$(10);
    this.nextOnEnterPressed$.next(true);
  }


  editMaterialClicked($event: MATERIAL) {
    this.startEditMaterialClicked.emit($event);
    this.scrollService.scrollToElement('materials');
  }
}
